export type NavigationSection = {
  id: string;
  label: string;
};

export type CareModeId = 'companion' | 'feline' | 'urgent';

export type CareMode = {
  id: CareModeId;
  label: string;
  blurb: string;
  accent: string;
  metal: string;
  tube: string;
};

export type CareChapter = {
  id: string;
  title: string;
  body: string;
};

export type ServiceItem = {
  title: string;
  body: string;
};

export type TrustQuote = {
  name: string;
  date: string;
  quote: string;
};

export const navigationSections: NavigationSection[] = [
  { id: 'care', label: 'Care' },
  { id: 'modes', label: 'Modes' },
  { id: 'services', label: 'Services' },
  { id: 'trust', label: 'Trust' },
  { id: 'visit', label: 'Visit' },
];

export const careModes: CareMode[] = [
  {
    id: 'companion',
    label: 'Companion',
    blurb: 'Everyday dogs who need steady hands and clear next steps.',
    accent: '#8FCB9B',
    metal: '#1F6B4F',
    tube: '#3D9B72',
  },
  {
    id: 'feline',
    label: 'Feline',
    blurb: 'Quiet rooms, unhurried exams, and cats who set the pace.',
    accent: '#A8C4D4',
    metal: '#3D5A6C',
    tube: '#6B8FA3',
  },
  {
    id: 'urgent',
    label: 'Urgent',
    blurb: 'When something feels wrong and you need the clinic phone now.',
    accent: '#E8A090',
    metal: '#8B3A2F',
    tube: '#C45C45',
  },
];

export const careChapters: CareChapter[] = [
  {
    id: 'consult',
    title: 'Listen first',
    body: 'A thorough consult starts with your story, then a calm exam. We translate findings into plain choices you can act on.',
  },
  {
    id: 'prevent',
    title: 'Stay ahead',
    body: 'Vaccines, parasite control, and wellness checks keep Bryanston pets out of the emergency room and in the park.',
  },
  {
    id: 'treat',
    title: 'Treat with care',
    body: 'From dental work to medical cases, the team works with the same focus reviewers praise: informed, personal, and clinical.',
  },
  {
    id: 'after',
    title: 'Send you home clear',
    body: 'You leave with a plan, not a fog of jargon. Call if anything shifts overnight. We would rather hear from you early.',
  },
];

export const serviceItems: ServiceItem[] = [
  {
    title: 'General consults',
    body: 'Sick visits, second opinions, and the check-ups that catch small changes early.',
  },
  {
    title: 'Preventive care',
    body: 'Vaccinations, deworming, flea and tick plans matched to Jozi seasons.',
  },
  {
    title: 'Surgery & dentistry',
    body: 'Planned procedures in a neat clinical setting with clear consent and aftercare.',
  },
  {
    title: 'Chronic support',
    body: 'Ongoing management for conditions like diabetes, with staff who know your pet by name.',
  },
  {
    title: 'Nutrition guidance',
    body: 'Practical feeding advice so home care matches what happens in the consult room.',
  },
  {
    title: 'Owner coaching',
    body: 'Straight answers on options, timelines, and what to watch for after you leave.',
  },
];

export const trustQuotes: TrustQuote[] = [
  {
    name: 'Fritz Klee',
    date: 'November 2020',
    quote:
      'Extremely passionate vets who will take care of your pets like they would their own. Staff is highly educated and informed of new research.',
  },
  {
    name: 'Wolfram Schüler',
    date: 'February 2021',
    quote: 'Have been going there since 7 years with my dog and the service is excellent.',
  },
  {
    name: 'Chad Bowring',
    date: 'May 2017',
    quote: 'They are a wonderful practice in general. Even the receptionists are refreshingly friendly and kind.',
  },
];

export const careModeLookup = Object.fromEntries(careModes.map((mode) => [mode.id, mode])) as Record<
  CareModeId,
  CareMode
>;
